import React from 'react';
import {
    Dialog,
    DialogFooter,
    DialogType,
    DefaultButton,
    FocusZone,
    FocusZoneDirection,
    IFocusZone,
    MessageBar,
    MessageBarType,
    SearchBox,
    Spinner,
    Stack,
    Text,
} from '@fluentui/react';
import { IInstanceOption, INSTANCE_TYPES } from '../../settings/instances';
import { InstanceTypeKey } from '../../settings/registry';

interface IInstancePickerDialogProps {
    instanceType: InstanceTypeKey;
    /** Narrows the list, e.g. to one extension type. */
    filter?: string;
    /** What the caller is trying to reach, named in the prompt. */
    settingLabel?: string;
    /** Skip the question and take this 0-based position. Set by ?instance=first or ?instance=<n>. */
    autoSelectIndex?: number;
    /** Pre-selected id, usually the last one the user worked with. */
    suggestedId?: string;
    /** Prepended choices outside the instance list - e.g. "Global" for a merged org-wide screen. */
    extraOptions?: IInstanceOption[];
    onSelect: (instanceId: string) => void;
    onCancel: () => void;
}

const MAX_VISIBLE = 200;

/**
 * "Which one of these are you working on?"
 *
 * A documentation link can name a setting without naming an instance of it - there is one Sub URL
 * setting, but one per storefront. This asks, and skips asking when there is only one answer.
 */
const InstancePickerDialog: React.FC<IInstancePickerDialogProps> = (props) => {
    const definition = INSTANCE_TYPES[props.instanceType];
    const [search, setSearch] = React.useState('');
    const List = definition.List;

    return (
        <List filter={props.filter}>
            {({ options, isLoading, isError }) => (
                <InstancePickerDialogContent
                    {...props}
                    pluralLabel={definition.pluralLabel}
                    typeLabel={definition.label}
                    options={options}
                    isLoading={isLoading}
                    isError={isError}
                    search={search}
                    onSearchChange={setSearch}
                />
            )}
        </List>
    );
};

interface IContentProps extends IInstancePickerDialogProps {
    pluralLabel: string;
    typeLabel: string;
    options: IInstanceOption[];
    isLoading: boolean;
    isError: boolean;
    search: string;
    onSearchChange: (value: string) => void;
}

const InstancePickerDialogContent: React.FC<IContentProps> = ({
    options,
    isLoading,
    isError,
    search,
    onSearchChange,
    onSelect,
    onCancel,
    settingLabel,
    suggestedId,
    autoSelectIndex,
    typeLabel,
    pluralLabel,
    extraOptions,
}) => {
    /*
     * One option means there is nothing to ask about, and a positional ?instance= says not to ask.
     * An index past the end is deliberately NOT clamped — the picker stays up and asks, rather than
     * quietly answering with the last instance as though the link had named it.
     */
    React.useEffect(() => {
        if (isLoading || isError) return;
        // Extra options are real choices: one instance plus "Global" is still a question.
        if (options.length === 1 && !(extraOptions?.length)) {
            onSelect(options[0].id);
            return;
        }
        if (autoSelectIndex !== undefined && autoSelectIndex < options.length) {
            onSelect(options[autoSelectIndex].id);
        }
    }, [isLoading, isError, options, onSelect, autoSelectIndex, extraOptions]);

    const listZoneRef = React.useRef<IFocusZone>(null);
    const term = search.trim().toLowerCase();
    const filtered = term
        ? options.filter(
              (o) => o.label.toLowerCase().includes(term) || (o.secondary ?? '').toLowerCase().includes(term)
          )
        : options;
    // The suggestion (usually the instance the user last worked with) leads the list, so the
    // likeliest answer is one Enter away.
    const ordered = suggestedId
        ? [...filtered.filter((o) => o.id === suggestedId), ...filtered.filter((o) => o.id !== suggestedId)]
        : filtered;
    // Extras lead unconditionally - "Global" stays the first option even above the suggestion.
    const filteredExtras = (extraOptions ?? []).filter(
        (o) => !term || o.label.toLowerCase().includes(term) || (o.secondary ?? '').toLowerCase().includes(term)
    );
    const visible = [...filteredExtras, ...ordered].slice(0, MAX_VISIBLE);

    return (
        <Dialog
            hidden={false}
            onDismiss={onCancel}
            dialogContentProps={{
                type: DialogType.normal,
                title: `Which ${typeLabel}?`,
                subText: settingLabel
                    ? `"${settingLabel}" is configured per ${typeLabel}. Choose the one you are working on.`
                    : `This setting is configured per ${typeLabel}.`,
            }}
            modalProps={{ isBlocking: false }}
            minWidth={480}
        >
            {isLoading && <Spinner label={`Loading ${pluralLabel.toLowerCase()}…`} />}
            {isError && (
                <MessageBar messageBarType={MessageBarType.error}>
                    Could not load {pluralLabel.toLowerCase()}. Try again, or navigate there from the menu.
                </MessageBar>
            )}
            {!isLoading && !isError && options.length === 0 && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    There are no {pluralLabel.toLowerCase()} in this organization yet.
                </MessageBar>
            )}
            {!isLoading && !isError && options.length > 1 && (
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    {options.length > 5 && (
                        <SearchBox
                            placeholder={`Search ${pluralLabel.toLowerCase()}…`}
                            value={search}
                            onChange={(_, v) => onSearchChange(v ?? '')}
                            onKeyDown={(ev) => {
                                // Arrow down drops from the search box into the option list.
                                if (ev.key === 'ArrowDown') {
                                    ev.preventDefault();
                                    listZoneRef.current?.focus();
                                }
                            }}
                        />
                    )}
                    {/* Arrow keys walk the options (wrapping); Enter picks the focused one. */}
                    <FocusZone
                        componentRef={listZoneRef}
                        direction={FocusZoneDirection.vertical}
                        isCircularNavigation
                    >
                    <Stack tokens={{ childrenGap: '0.5em' }} styles={{ root: { maxHeight: 360, overflowY: 'auto' } }}>
                        {visible.map((option) => (
                            <DefaultButton
                                key={option.id}
                                primary={option.id === suggestedId}
                                onClick={() => onSelect(option.id)}
                                /*
                                 * A Fluent button centres its children: `.ms-Button-flexContainer`
                                 * is a centred flex row, so custom content lands in the middle and
                                 * each row starts at a different offset depending on how long its
                                 * label is. Left-aligning the container and letting it fill the
                                 * button is what makes the list read as a list.
                                 */
                                styles={{
                                    root: { height: 'auto', padding: '0.5em 1em', textAlign: 'left' },
                                    flexContainer: { justifyContent: 'flex-start', width: '100%' },
                                    textContainer: { textAlign: 'left' },
                                }}
                            >
                                <Stack horizontalAlign='start' styles={{ root: { width: '100%' } }}>
                                    <Text block>{option.label}</Text>
                                    {option.secondary && (
                                        <Text block variant='small' styles={{ root: { opacity: 0.75 } }}>
                                            {option.secondary}
                                        </Text>
                                    )}
                                </Stack>
                            </DefaultButton>
                        ))}
                        {filtered.length > visible.length && (
                            <Text variant='small'>
                                Showing the first {MAX_VISIBLE} of {filtered.length}. Narrow the search to see more.
                            </Text>
                        )}
                        {term && filtered.length === 0 && <Text variant='small'>No matches.</Text>}
                    </Stack>
                    </FocusZone>
                </Stack>
            )}
            <DialogFooter>
                <DefaultButton text='Cancel' onClick={onCancel} />
            </DialogFooter>
        </Dialog>
    );
};

export default InstancePickerDialog;
